import type {
  AddMemberInput,
  ApiErrorCode,
  BoardDto,
  BoardListFilter,
  BoardListSort,
  BoardSnapshot,
  BoardSummaryDto,
  CreateBoardInput,
  MemberDto,
  MemberRole,
  RequestLinkInput,
  UpdateBoardInput,
  UpdateProfileInput,
  UploadDto,
  UserDto,
  VersionDto,
} from '@miroom/shared';

/** Error raised by API ports; `code` mirrors the server's ApiErrorCode, `network` = no response. */
export class ApiError extends Error {
  constructor(
    readonly code: ApiErrorCode | 'network',
    message: string,
    readonly status = 0,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

export interface VerifiedLink {
  user: UserDto;
  next: string;
  created: boolean;
}

export interface AuthApi {
  me(): Promise<UserDto | null>;
  /** Emails a one-time sign-in link (creates the account on first use). */
  requestLink(input: RequestLinkInput): Promise<void>;
  /** Redeems the link token and signs in. */
  verifyLink(token: string): Promise<VerifiedLink>;
  logout(): Promise<void>;
  /** Ends every session of the current user. */
  logoutAll(): Promise<void>;
  updateProfile(patch: UpdateProfileInput): Promise<UserDto>;
  /** The user confirms by typing their email. */
  deleteAccount(email: string): Promise<void>;
}

export interface BoardsApi {
  list(filter: BoardListFilter, search?: string, sort?: BoardListSort): Promise<BoardSummaryDto[]>;
  create(input: CreateBoardInput): Promise<BoardDto>;
  get(id: string): Promise<BoardDto>;
  update(id: string, patch: UpdateBoardInput): Promise<BoardDto>;
  remove(id: string): Promise<void>;
  restore(id: string): Promise<BoardDto>;
  destroy(id: string): Promise<void>;
  duplicate(id: string): Promise<BoardDto>;
  setStarred(id: string, starred: boolean): Promise<void>;
  setThumbnail(id: string, dataUrl: string): Promise<void>;

  listMembers(id: string): Promise<MemberDto[]>;
  addMember(id: string, input: AddMemberInput): Promise<MemberDto>;
  updateMember(id: string, userId: string, role: MemberRole): Promise<MemberDto>;
  removeMember(id: string, userId: string): Promise<void>;

  listVersions(id: string): Promise<VersionDto[]>;
  createVersion(id: string, name: string): Promise<VersionDto>;
  getVersion(id: string, versionId: string): Promise<BoardSnapshot>;
  restoreVersion(id: string, versionId: string): Promise<void>;

  upload(id: string, file: Blob, onProgress?: (fraction: number) => void): Promise<UploadDto>;
  /** Copies images of other boards into this one; returns old URL -> new URL. */
  copyUploads(id: string, urls: string[]): Promise<Record<string, string>>;
  exportJson(id: string): Promise<BoardSnapshot>;
  importJson(snapshot: unknown, title?: string): Promise<BoardDto>;
}
